import { type ReactNode, useMemo } from 'react'

import {
  Badge,
  Box,
  Flex,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'

import {
  Box as DarkBox,
  Flex as DarkFlex,
  Table as DarkTable,
  TableBody as DarkTableBody,
  TableCell as DarkTableCell,
  TableHeader as DarkTableHeader,
  TableHeaderCell as DarkTableHeaderCell,
  TableRow as DarkTableRow,
  Text as DarkText,
  tokenPrefix,
  tokenVar,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { Button } from '@/components/ui'
import { VideoPlayer } from '@/features/video-player'
import {
  GridAnalysisJob,
  TransitionDirection,
} from '@/graphql/generated/schemas'

import { GridModalHeader } from '../../components/GridModalHeader'
import { scrollbarStyle } from '../../styles/styles'
import { IncidentOptionI } from '../../types/types'

interface DirectionBadgeIProps {
  colorScheme?: ColorScheme
  isMatch: boolean
}

const DirectionBadge = ({ colorScheme, isMatch }: DirectionBadgeIProps) => {
  if (colorScheme) {
    const tokenPathPrefix = tokenPrefix(colorScheme)

    return (
      <DarkBox
        as='span'
        bg={tokenVar(
          `colors.${tokenPathPrefix}status.${
            isMatch ? 'success-bg' : 'error-bg'
          }`
        )}
        color={tokenVar(
          `colors.${tokenPathPrefix}status.${
            isMatch ? 'success-text' : 'error-text'
          }`
        )}
        fontSize='12px'
        fontWeight='600'
        px='8px'
        py='4px'
      >
        {isMatch ? 'Match' : 'Mismatch'}
      </DarkBox>
    )
  }

  return (
    <Badge colorScheme={isMatch ? 'green' : 'red'}>
      {isMatch ? 'Match' : 'Mismatch'}
    </Badge>
  )
}

interface CountsComparisonTableIProps {
  borderColor: string
  colorScheme?: ColorScheme
  computedIn: number
  computedOut: number
  isInMatch: boolean
  isOutMatch: boolean
  labeledIn: number
  labeledOut: number
  rowBackground: string
}

const CountsComparisonTable = ({
  borderColor,
  colorScheme,
  computedIn,
  computedOut,
  isInMatch,
  isOutMatch,
  labeledIn,
  labeledOut,
  rowBackground,
}: CountsComparisonTableIProps) => {
  if (colorScheme) {
    return (
      <DarkBox mt='16px'>
        <DarkTable colorScheme={colorScheme}>
          <DarkTableHeader>
            <DarkTableHeaderCell flexGrow={1}>Direction</DarkTableHeaderCell>
            <DarkTableHeaderCell flexGrow={1}>Expected</DarkTableHeaderCell>
            <DarkTableHeaderCell flexGrow={1}>Computed</DarkTableHeaderCell>
            <DarkTableHeaderCell flexGrow={1}>Result</DarkTableHeaderCell>
          </DarkTableHeader>
          <DarkTableBody>
            <DarkTableRow>
              <DarkTableCell flexGrow={1}>IN</DarkTableCell>
              <DarkTableCell flexGrow={1}>{labeledIn}</DarkTableCell>
              <DarkTableCell flexGrow={1}>{computedIn}</DarkTableCell>
              <DarkTableCell flexGrow={1}>
                <DirectionBadge colorScheme={colorScheme} isMatch={isInMatch} />
              </DarkTableCell>
            </DarkTableRow>
            <DarkTableRow isAlternate>
              <DarkTableCell flexGrow={1}>OUT</DarkTableCell>
              <DarkTableCell flexGrow={1}>{labeledOut}</DarkTableCell>
              <DarkTableCell flexGrow={1}>{computedOut}</DarkTableCell>
              <DarkTableCell flexGrow={1}>
                <DirectionBadge
                  colorScheme={colorScheme}
                  isMatch={isOutMatch}
                />
              </DarkTableCell>
            </DarkTableRow>
          </DarkTableBody>
        </DarkTable>
      </DarkBox>
    )
  }

  return (
    <Box
      border='1px solid'
      borderColor={borderColor}
      borderRadius='lg'
      mt={4}
      overflow='hidden'
    >
      <Table size='sm' sx={{ tableLayout: 'fixed' }}>
        <Thead>
          <Tr bg={rowBackground}>
            <Th fontSize='sm' width='25%'>
              Direction
            </Th>
            <Th fontSize='sm' width='25%'>
              Expected
            </Th>
            <Th fontSize='sm' width='25%'>
              Computed
            </Th>
            <Th fontSize='sm' width='25%'>
              Result
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td fontSize='sm' fontWeight='bold' py={2}>
              IN
            </Td>
            <Td fontSize='sm' py={2}>
              {labeledIn}
            </Td>
            <Td fontSize='sm' py={2}>
              {computedIn}
            </Td>
            <Td py={2}>
              <DirectionBadge isMatch={isInMatch} />
            </Td>
          </Tr>
          <Tr>
            <Td fontSize='sm' fontWeight='bold' py={2}>
              OUT
            </Td>
            <Td fontSize='sm' py={2}>
              {labeledOut}
            </Td>
            <Td fontSize='sm' py={2}>
              {computedOut}
            </Td>
            <Td py={2}>
              <DirectionBadge isMatch={isOutMatch} />
            </Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>
  )
}

const TransitionItem = ({
  children,
  colorScheme,
}: {
  children: ReactNode
  colorScheme?: ColorScheme
}) => {
  if (colorScheme) {
    const tokenPathPrefix = tokenPrefix(colorScheme)

    return (
      <DarkBox
        bg={tokenVar(`colors.${tokenPathPrefix}bg.card`)}
        border='1px solid'
        borderColor={tokenVar(`colors.${tokenPathPrefix}border.default`)}
        borderRadius='4px'
        px='12px'
        py='8px'
      >
        {children}
      </DarkBox>
    )
  }

  return (
    <Box
      bg='gray.50'
      border='1px solid'
      borderColor='gray.200'
      borderRadius='md'
      px={3}
      py={2}
    >
      {children}
    </Box>
  )
}

interface GridTestSuccessCardIProps {
  colorScheme?: ColorScheme
  job?: GridAnalysisJob
  labeledIn: number
  labeledOut: number
  isSaving: boolean
  onClose: () => void
  onSelectAnotherIncident: () => void
  onSaveGrid?: () => void
  selectedIncident: IncidentOptionI | null
}

export const GridTestSuccessCard = ({
  colorScheme,
  job,
  labeledIn,
  labeledOut,
  isSaving,
  onClose,
  onSelectAnotherIncident,
  onSaveGrid,
  selectedIncident,
}: GridTestSuccessCardIProps) => {
  const transitions = job?.transitions ?? []
  const [computedIn, computedOut] = useMemo(
    () => [
      transitions.filter((t) => t.direction === TransitionDirection.In).length,
      transitions.filter((t) => t.direction === TransitionDirection.Out).length,
    ],
    [transitions]
  )
  const isInMatch = computedIn === labeledIn
  const isOutMatch = computedOut === labeledOut
  const tokenPathPrefix = colorScheme ? tokenPrefix(colorScheme) : ''
  const borderColor = colorScheme
    ? tokenVar(`colors.${tokenPathPrefix}border.default`)
    : 'gray.200'
  const mutedTextColor = colorScheme
    ? tokenVar(`colors.${tokenPathPrefix}text.secondary`)
    : 'gray.500'
  const rowBackground = colorScheme
    ? tokenVar(`colors.${tokenPathPrefix}bg.table-row-alt`)
    : 'gray.50'

  if (colorScheme) {
    return (
      <>
        {job?.annotatedVideoUrl && (
          <DarkBox borderRadius='12px' mt='16px' overflow='hidden'>
            <VideoPlayer
              showControls
              showToggleLiveButton={false}
              suppressGlobalPlayState
              url={job.annotatedVideoUrl}
            />
          </DarkBox>
        )}

        <CountsComparisonTable
          borderColor={borderColor}
          colorScheme={colorScheme}
          computedIn={computedIn}
          computedOut={computedOut}
          isInMatch={isInMatch}
          isOutMatch={isOutMatch}
          labeledIn={labeledIn}
          labeledOut={labeledOut}
          rowBackground={rowBackground}
        />

        <DarkText fontSize='16px' fontWeight='bold' mt='20px'>
          People Detected
        </DarkText>
        <DarkFlex
          direction='column'
          gap='6px'
          maxHeight='180px'
          mt='8px'
          overflowY='auto'
        >
          {transitions.length === 0 ? (
            <TransitionItem colorScheme={colorScheme}>
              <DarkText color={mutedTextColor} fontSize='14px'>
                No transitions detected
              </DarkText>
            </TransitionItem>
          ) : (
            transitions.map((transition, index) => (
              <TransitionItem
                colorScheme={colorScheme}
                key={`${transition.personId}-${index}`}
              >
                <DarkText fontSize='14px'>
                  Person #{transition.personId} · {transition.direction} ·{' '}
                  {transition.timestamp.toFixed(2)}s
                </DarkText>
              </TransitionItem>
            ))
          )}
        </DarkFlex>
      </>
    )
  }

  const content = (
    <>
      {job?.annotatedVideoUrl && (
        <Box borderRadius='xl' mt={4} overflow='hidden'>
          <VideoPlayer
            showControls
            showToggleLiveButton={false}
            suppressGlobalPlayState
            url={job.annotatedVideoUrl}
          />
        </Box>
      )}

      <CountsComparisonTable
        borderColor={borderColor}
        computedIn={computedIn}
        computedOut={computedOut}
        isInMatch={isInMatch}
        isOutMatch={isOutMatch}
        labeledIn={labeledIn}
        labeledOut={labeledOut}
        rowBackground={rowBackground}
      />

      <Text fontSize='md' fontWeight='bold' mt={5}>
        People Detected
      </Text>
      <Flex
        direction='column'
        gap={1.5}
        maxH='180px'
        mt={2}
        overflowY='auto'
        sx={scrollbarStyle}
      >
        {transitions.length === 0 ? (
          <TransitionItem>
            <Text color={mutedTextColor} fontSize='sm'>
              No transitions detected
            </Text>
          </TransitionItem>
        ) : (
          transitions.map((t, i) => (
            <TransitionItem key={`${t.personId}-${i}`}>
              <Text fontSize='sm'>
                Person #{t.personId} · {t.direction} · {t.timestamp.toFixed(2)}s
              </Text>
            </TransitionItem>
          ))
        )}
      </Flex>
    </>
  )

  return (
    <>
      <ModalHeader p={0} w='100%'>
        <GridModalHeader
          subtitle={`Review the test grid's results for ${selectedIncident?.incidentId}`}
          title='Grid Testing Results'
        />
      </ModalHeader>
      <ModalBody p={0}>{content}</ModalBody>
      <ModalFooter mt={6} p={0}>
        <Flex direction='row' gap={3} justifyContent='flex-end'>
          <Button mr={0} onClick={onClose} variant='secondary'>
            Cancel
          </Button>
          <Button mr={0} onClick={onSelectAnotherIncident} variant='secondary'>
            Select Another Incident
          </Button>
          <Button isDisabled={isSaving} onClick={onSaveGrid}>
            Save New Camera Grid
          </Button>
        </Flex>
      </ModalFooter>
    </>
  )
}
